import React, { useState, useEffect } from "react";
import {
	Autocomplete,
	TextField,
	CircularProgress,
	Box,
	Alert,
	Tooltip,
	IconButton,
	Grid2,
	Button,
	FormHelperText,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Card,
	CardContent,
	Typography,
} from "@mui/material";
import customHttp from "../../services/customHTTP";
import Datagrid from "../../components/Datagrid/Datagrid";
import { useSite } from "../../context/siteContext";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ManageSites = () => {
	const { selectedSite, fetchSites } = useSite();
	const [customers, setCustomers] = useState([]);
	const [sites, setSites] = useState([]);
	const [error, setError] = useState("");
	const [formErrors, setFormErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [siteError, setSiteError] = useState("");
	const [sitesLoading, setSitesLoading] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [selectedSiteData, setSelectedSiteData] = useState(null);
	const [formData, setFormData] = useState({
		default_legal_entity: "BPA",
		site_currency: "usd",
		site_name: "",
		warehouse: "",
	});
	const [editFormLoading, setEditFormLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
	const [emptySites, setEmptySites] = useState(false);

	useEffect(() => {
		const fetchCustomers = async () => {
			try {
				const response = await customHttp.get("/cus_sites/getcustomers");
				if (response.data.success) {
					// Filter customers where customer_name is not 'EMPTY', '', or null
					const filteredCustomers = response.data.data.filter(
						(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
					);
					setCustomers(filteredCustomers);
				} else {
					setError("Failed to fetch customers.");
				}
			} catch (err) {
				setError("Failed to fetch customers: " + err.message);
			} finally {
				setLoading(false);
			}
		};

		fetchCustomers();
	}, [selectedSite]);

	useEffect(() => {
		// Simulate an async operation, e.g., fetching data or processing
		const loadForm = () => {
			setTimeout(() => {
				setEditFormLoading(false); // Set loading to false once data is loaded
			}, 2000); // Simulating loading delay of 2 seconds
		};

		if (selectedSiteData) {
			setEditFormLoading(true);
			setFormData({
				default_legal_entity: selectedSiteData.site_legal_entity ?? "AUSA",
				site_currency: selectedSiteData.site_currency ?? "usd",
				site_name: selectedSiteData.site_name ?? "",
				warehouse: selectedSiteData.warehouse ?? "",
			});
			loadForm();
		}
	}, [selectedSiteData]);

	const handleChange = (event) => {
		const { name, value } = event.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		// Validate the form fields
		const newErrors = {};
		if (!formData.site_name) newErrors.site_name = "Site name is required.";
		if (!formData.warehouse) newErrors.warehouse = "Warehouse is required.";
		if (!formData.default_legal_entity) newErrors.default_legal_entity = "Legal entity is required.";
		if (!formData.site_currency) newErrors.site_currency = "Currency is required.";

		if (Object.keys(newErrors).length > 0) {
			setFormErrors(newErrors);
			return;
		}

		// Clear errors if no issues
		setFormErrors({});

		const req_body = {
			site_id: selectedSiteData.site_id,
			cus_id: selectedSiteData.cus_id,
			site_name: formData.site_name,
			warehouse: formData.warehouse,
			site_legal_entity: formData.default_legal_entity,
			site_currency: formData.site_currency,
		};
		setIsSubmitting(true);
		setIsSubmitSuccess(false);

		try {
			// Make the POST request
			const response = await customHttp.post("/cus_sites/updatesites", req_body);
			console.log(response);
			// Handle success response
			if (response.data.success) {
				try {
					const response = await customHttp.get(`/cus_sites/getsites/${selectedSiteData.cus_id}`);
					if (response.data.success) {
						setSites(response.data.data);
						setSitesLoading(false);
					} else {
						setSiteError("Failed to fetch sites.");
					}
				} catch (err) {
					setSiteError("Failed to fetch sites: " + err.message);
				}

				setIsSubmitting(false);
				setIsSubmitSuccess(true);
				// Show success alert
				Swal.fire({
					icon: "success",
					title: "Site updated successfully",
					text: "The site meta has been updated.",
				});
				fetchSites();
				setSelectedSiteData(null);

				setTimeout(() => {
					setIsSubmitting(false);
					setIsSubmitSuccess(false);
				}, 2000);
			} else {
				// Show error alert
				setIsSubmitting(false);
				setIsSubmitSuccess(false);
				Swal.fire({
					icon: "error",
					title: "Error",
					text: response.message || "An error occurred while updating the site.",
				});
			}
		} catch (error) {
			// Show error alert on request failure
			setIsSubmitting(false);
			setIsSubmitSuccess(false);
			Swal.fire({
				icon: "error",
				title: "Error",
				text: "An unexpected error occurred.",
			});
		}
	};

	const handleShowSites = async (customer) => {
		setSitesLoading(true);
		try {
			const response = await customHttp.get(`/cus_sites/getsites/${customer.cus_id}`);
			if (response.data.success) {
				console.log(response.data.data);
				setSites(response.data.data);
				setSitesLoading(false);
				if (response.data.data.length === 0) {
					setEmptySites(true);
				} else {
					setEmptySites(false);
				}
			} else {
				setSiteError("Failed to fetch sites.");
			}
		} catch (err) {
			setSiteError("Failed to fetch sites: " + err.message);
		}
	};

	const handleEditSite = async (selectedSiteData) => {
		console.log(selectedSiteData);
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}

	let label = "Select a customer to view locations";

	// Check if siteLoading is true

	if (emptySites) {
		label = "No Sites Found for Customer";
	} else if (!sitesLoading && sites.length === 0) {
		label = "Select Customer to view locations";
	} else if (sitesLoading) {
		label = "Loading...";
	} else if (!sitesLoading && sites.length > 0) {
		label = "Select Customer Site";
	}

	return (
		<div>
			{/* Autocomplete Dropdown */}
			<div
				style={{
					marginBottom: "20px",
					marginTop: "20px",
					display: "flex",
					flexDirection: "row",
					columnGap: "20px",
				}}>
				<Autocomplete
					options={customers}
					key={(option) => option.cus_id}
					getOptionLabel={(option) => option.customer_name}
					value={selectedCustomer}
					onChange={(event, newValue) => {
						setSelectedCustomer(newValue);
						if (newValue) {
							handleShowSites(newValue);
						} else {
							setSelectedSiteData(null); // Clear selected site when customer is cleared
						}
					}}
					renderInput={(params) => <TextField {...params} label="Select a customer" variant="outlined" />}
					fullWidth
					size="small"
				/>

				<Autocomplete
					options={sites}
					getOptionLabel={(option) =>
						`${option.customer_site_name} - ${option.customer_site_number}` || "No sites available"
					}
					value={selectedSiteData}
					onChange={(event, newValue) => {
						console.log(newValue);
						setSelectedSiteData(newValue);
						if (newValue) {
							handleEditSite(newValue);
						}
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label={label}
							variant="outlined"
							placeholder={
								sitesLoading
									? "Loading..."
									: emptySites
									? "No Sites found for selected Customer"
									: "Select a customer to view locations"
							}
						/>
					)}
					fullWidth
					size="small"
					disabled={sitesLoading || sites.length === 0 || !selectedCustomer}
				/>
			</div>

			{selectedSiteData && (
				<>
					<div
						style={{
							marginBottom: "20px",
							marginTop: "20px",
							display: "flex",
							flexDirection: "row",
							columnGap: "20px",
							width: "100%",
						}}>
						<Box
							flex="1"
							minWidth="300px"
							padding="20px"
							sx={{
								backgroundColor: "#ffffff",
								borderRadius: "8px",
								boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
							}}>
							<Typography
								variant="h6"
								sx={{
									color: "var(--primary-main)",
									fontWeight: "bold",
									marginBottom: "10px",
								}}>
								Customer Information
							</Typography>
							<Typography variant="body1" color="text.secondary">
								<strong>Customer Name:</strong> {selectedSiteData.customer_site_name}
							</Typography>
							<Typography variant="body1" color="text.secondary">
								<strong>Customer #:</strong> {selectedSiteData.customer_site_number}
							</Typography>
						</Box>
						<Box
							flex="1"
							minWidth="300px"
							padding="20px"
							sx={{
								backgroundColor: "#ffffff",
								borderRadius: "8px",
								boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
							}}>
							<Typography
								variant="h6"
								sx={{
									color: "var(--primary-main)",
									fontWeight: "bold",
									marginBottom: "10px",
								}}>
								Address Information
							</Typography>
							<Typography variant="body1" color="text.secondary">
								<strong>Address Name:</strong> {selectedSiteData.address_name_ln}
							</Typography>
							<Typography variant="body1" color="text.secondary">
								<strong>Full Address:</strong> {selectedSiteData.address_full_ln}
							</Typography>
						</Box>

						<Box
							flex="1"
							minWidth="300px"
							padding="20px"
							sx={{
								backgroundColor: "#ffffff",
								borderRadius: "8px",
								boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
							}}>
							<Typography
								variant="h6"
								sx={{
									color: "var(--primary-main)",
									fontWeight: "bold",
									marginBottom: "10px",
								}}>
								CCR Information
							</Typography>

							<Typography variant="body1" color="text.secondary">
								<strong>CCR Name:</strong> {selectedSiteData.ccr_name}
							</Typography>
							<Typography variant="body1" color="text.secondary">
								<strong>CCR Email:</strong> {selectedSiteData.ccr_email}
							</Typography>
							<Typography variant="body1" color="text.secondary">
								<strong>CCR Phone:</strong> {selectedSiteData.ccr_phone}
							</Typography>
						</Box>
					</div>

					<form onSubmit={handleSubmit}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
							<Box sx={{ display: "flex", gap: 2 }}>
								<FormControl fullWidth>
									<InputLabel id="demo-simple-select-label">Default Company</InputLabel>
									<Select
										labelId="demo-simple-select-label"
										id="demo-simple-select"
										value={formData.default_legal_entity ?? "AUSA"}
										label="Default Comapny"
										name="default_legal_entity"
										onChange={handleChange}
										size="small"
										error={!!formErrors.default_legal_entity}
										helperText={formErrors.default_legal_entity}>
										<MenuItem value="BPA">BPA</MenuItem>
										<MenuItem value="BPR">BPR</MenuItem>
									</Select>
								</FormControl>

								<FormControl fullWidth>
									<InputLabel id="demo-simple-select-label1">Site Currency</InputLabel>
									<Select
										labelId="demo-simple-select-label1"
										id="demo-simple-select1"
										value={formData.site_currency ?? "usd"}
										label="Site Currency"
										onChange={handleChange}
										name="site_currency"
										size="small"
										error={!!formErrors.site_currency}
										helperText={formErrors.site_currency}>
										<MenuItem value="usd">USD</MenuItem>
										<MenuItem value="cad">CAD</MenuItem>
										<MenuItem value="gbp">GBP</MenuItem>
										<MenuItem value="eur">EUR</MenuItem>
										<MenuItem value="sgd">SGD</MenuItem>
										<MenuItem value="inr">INR</MenuItem>
									</Select>
								</FormControl>
							</Box>

							<Box sx={{ display: "flex", gap: 2 }}>
								<TextField
									label="Site"
									fullWidth
									name="site_name"
									value={formData.site_name ?? ""}
									onChange={handleChange}
									size="small"
									error={!!formErrors.site_name}
									helperText={formErrors.site_name}
								/>

								<TextField
									label="Warehouse"
									fullWidth
									name="warehouse"
									value={formData.warehouse ?? ""}
									onChange={handleChange}
									size="small"
									error={!!formErrors.warehouse}
									helperText={formErrors.warehouse}
								/>
							</Box>

							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button variant="contained" color="primary" type="submit">
									{isSubmitting ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
											Submitting...
										</>
									) : isSubmitSuccess ? (
										<>
											<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
											Submitted
										</>
									) : (
										"Submit"
									)}
								</Button>
							</Box>
						</Box>
					</form>
				</>
			)}
		</div>
	);
};

export default ManageSites;